import {
  Box,
  Text,
  Badge,
  Code,
  VStack,
  Icon,
  useBreakpointValue,
  IconButton,
  Tooltip,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  useDisclosure,
  Checkbox,
  useColorModeValue,
} from '@chakra-ui/react'
import { CheckIcon, QuestionOutlineIcon, ExternalLinkIcon } from '@chakra-ui/icons'
import { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { FiEye, FiCheck, FiInfo } from 'react-icons/fi'
import { useRefundOperations, useVerifyRefundOperation } from '@/hooks/useAdminData'
import { DataTable } from '@/components/common/DataTable'
import { ColumnConfig, FilterConfig, ActionButton } from '@/components/common/DataTable/types'
import { ChainBadge } from '@/components/common/ChainBadge'
import { useCopyableCodeStyles } from '@/components/common/useCopyableCodeStyles'
import { StatusBadge } from '@/components/common/StatusBadge'
import { TokenAmountDisplay } from '@/components/common/TokenAmountDisplay'
import { RefundOperation } from '@/types'
import { getChainsByNetwork, parseChainId } from '@/constants/chains'
import { formatRefundAmount, truncateHash, getExplorerTxUrl, formatDate, formatDateTime, formatTime } from '@/utils/formatUtils'
import { RefundDetailsModal } from './RefundDetailsModal'
import { useToast } from '@/hooks/useToast'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { triggerBrowserDownload } from '@/services/admin/baseService'
import { refundService } from '@/services/admin/refundService'

// Verification status component
const VerifiedBadge = ({ isVerified, verifiedAt }: { isVerified?: boolean; verifiedAt?: string }) => {
  const { t } = useTranslation()
  const showText = useBreakpointValue({ base: false, md: true })

  if (isVerified) {
    return (
      <Badge
        colorScheme="green"
        variant="solid"
        display="flex"
        alignItems="center"
        gap={showText ? 1 : 0}
        px={showText ? 2 : 1.5}
        title={verifiedAt ? `Verified at: ${formatDateTime(verifiedAt)}` : t('refunds.status.verified')}
      >
        <Icon as={CheckIcon} boxSize={2.5} />
        {showText && <Text fontSize="xs">Verified</Text>}
      </Badge>
    )
  }

  return (
    <Badge
      colorScheme="gray"
      variant="solid"
      display="flex"
      alignItems="center"
      gap={showText ? 1 : 0}
      px={showText ? 2 : 1.5}
      title="Not verified"
    >
      <Icon as={QuestionOutlineIcon} boxSize={2.5} />
      {showText && <Text fontSize="xs">Unverified</Text>}
    </Badge>
  )
}

export interface RefundOperationsTableProps {
  onRefundSelect?: (refund: RefundOperation) => void
  showFilters?: boolean
  defaultPageSize?: number
}

export function RefundOperationsTable({
  onRefundSelect,
  showFilters = true,
  defaultPageSize = 20
}: RefundOperationsTableProps) {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const { success: showToastSuccess, error: showToastError } = useToast()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(defaultPageSize)
  const [filters, setFilters] = useState<Record<string, any>>({})
  const [, setSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)
  const [selectedRefund, setSelectedRefund] = useState<RefundOperation | null>(null)
  const [forceVerification, setForceVerification] = useState(false)
  const [selectedRefundIdForDetails, setSelectedRefundIdForDetails] = useState<string>('')
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isDetailsOpen, onOpen: onDetailsOpen, onClose: onDetailsClose } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)

  // Theme-aware colors
  const dialogBg = useColorModeValue('white', 'gray.800')
  const infoBg = useColorModeValue('gray.50', 'gray.700')
  const infoTextColor = useColorModeValue('gray.800', 'gray.100')
  const copyableCodeStyles = useCopyableCodeStyles()

  const normalizedIsVerified = filters.isVerified === 'true' ? true : filters.isVerified === 'false' ? false : undefined

  const { data, isLoading, error, refetch, isFetching } = useRefundOperations({
    page,
    limit: pageSize,
    search: filters.search || '',
    status: filters.status || '',
    chain_id: filters.chainId || undefined,
    merchant_id: filters.merchantId || undefined,
    is_verified: normalizedIsVerified,
    date_from: filters.dateFrom || undefined,
    date_to: filters.dateTo || undefined,
  })

  const verifyMutation = useVerifyRefundOperation()

  // Copy to clipboard utility
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      showToastSuccess({
        title: 'Copied to clipboard',
        description: `${label} copied successfully`,
        duration: 2000,
        isClosable: true,
      })
    } catch (error) {
      showToastError({
        title: 'Copy failed',
        description: 'Unable to copy to clipboard',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const resolveRefundChainId = useCallback((refund?: RefundOperation | null) => {
    const normalizeCandidate = (candidate: unknown): string | null => {
      if (candidate === null || candidate === undefined) {
        return null
      }
      if (typeof candidate === 'number') {
        if (!Number.isFinite(candidate)) {
          return null
        }
        const parsed = parseChainId(candidate)
        return parsed && parsed !== '0' ? parsed : String(candidate)
      }
      if (typeof candidate === 'string') {
        const trimmed = candidate.trim()
        if (!trimmed) {
          return null
        }
        const lowerTrimmed = trimmed.toLowerCase()
        if (
          lowerTrimmed.startsWith('chain-') ||
          lowerTrimmed.startsWith('chain_') ||
          (lowerTrimmed.startsWith('chn_') && !/\d/u.test(lowerTrimmed))
        ) {
          return null
        }
        const parsed = parseChainId(trimmed)
        if (parsed && parsed !== '0') {
          return parsed
        }
        return trimmed
      }
      return null
    }

    if (!refund) return 'unknown'

    const primary = normalizeCandidate((refund as any).chain_id ?? (refund as any).chainId)
    if (primary) {
      return primary
    }

    const candidates: Array<unknown> = [
      refund.chain_numeric_id,
      (refund as any).chainNumericId,
      refund.chain_code,
      (refund as any).chainCode,
      refund.chain_name,
      (refund as any).chainName,
      (refund as any).registry_chain_id,
      (refund as any).registryChainId,
    ]

    const orderInfo = (refund as any).order
    if (orderInfo && typeof orderInfo === 'object') {
      candidates.push(
        (orderInfo as any).chainId,
        (orderInfo as any).chain_id,
        (orderInfo as any).chain_numeric_id,
        (orderInfo as any).chainNumericId,
        (orderInfo as any).chain_code,
        (orderInfo as any).chainCode,
      )
    }

    for (const candidate of candidates) {
      const normalized = normalizeCandidate(candidate)
      if (normalized) {
        return normalized
      }
    }

    return 'unknown'
  }, [])

  const splitOrderId = useCallback((id?: string | null) => {
    if (!id) {
      return []
    }
    const trimmed = id.trim()
    if (!trimmed) {
      return []
    }
    if (trimmed.length <= 18) {
      return [trimmed]
    }
    const maxSegments = 3
    const segmentLength = Math.ceil(trimmed.length / maxSegments)
    const segments: string[] = []
    for (let i = 0; i < maxSegments; i += 1) {
      const start = i * segmentLength
      const part = trimmed.slice(start, start + segmentLength)
      if (part) {
        segments.push(part)
      }
    }
    return segments
  }, [])

  // Get status text
  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      'ros_pending': t('refunds.status.pending'),
      'ros_refunded': t('refunds.status.refunded'),
      'ros_failed': t('refunds.status.failed'),
    }
    return statusMap[status] || status
  }


  // Handle verification
  const handleVerify = (refund: RefundOperation) => {
    setSelectedRefund(refund)
    setForceVerification(false)
    onOpen()
  }

  // Handle view details
  const handleViewDetails = (refund: RefundOperation) => {
    setSelectedRefundIdForDetails(refund.id)
    onDetailsOpen()
  }

  const confirmVerify = async () => {
    if (!selectedRefund) return

    try {
      await verifyMutation.mutateAsync({
        id: selectedRefund.id,
        data: { force_verification: forceVerification }
      })

      showToastSuccess({
        title: t('refunds.verify.success'),
        duration: 3000,
        isClosable: true,
      })

      onClose()
    } catch (error: any) {
      showToastError({
        title: t('refunds.verify.error'),
        description: error?.message,
        duration: 5000,
        isClosable: true,
      })
    }
  }

  // Define table columns
  const columns: ColumnConfig<RefundOperation>[] = [
    {
      key: 'orderId',
      title: t('refunds.table.orderId'),
      width: '18%',
      sortable: true,
      render: (value, row) => {
        const rawOrderId = value || row.order_id || row.orderId || ''
        const orderId = typeof rawOrderId === 'string' ? rawOrderId : String(rawOrderId || '')

        if (!orderId) {
          return <Text fontSize="xs" color="gray.500">{t('common.unknown')}</Text>
        }

        const segments = splitOrderId(orderId)

        return (
          <Tooltip label={t('refunds.actions.copyOrderId')}>
            <Box
              minH="44px"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="flex-start"
            >
              <Code
                as="div"
                fontSize="xs"
                bg="transparent"
                cursor="pointer"
                wordBreak="break-word"
                whiteSpace="pre-line"
                _hover={copyableCodeStyles.hover}
                _active={copyableCodeStyles.active}
                onClick={() => copyToClipboard(orderId, 'Order ID')}
              >
                {segments.length > 0 ? segments.join('\n') : orderId}
              </Code>
            </Box>
          </Tooltip>
        )
      },
    },
    {
      key: 'refund_amount',
      title: t('refunds.table.refundAmount'),
      width: '12%',
      sortable: true,
      render: (_, row) => {
        const refundCount = row.refund_meta_count || 1

        // Use total_refund_amount if available (for multiple refunds), otherwise use refund_amount
        const amountInSmallestUnit = row.total_refund_amount || row.refund_amount || '0'

        // Use unified format utility
        const formattedAmount = formatRefundAmount(
          amountInSmallestUnit,
          resolveRefundChainId(row),
          row.token_address,
          row.token_symbol,
          row.tokenDecimals ?? row.token_decimals
        )

        return (
          <Box>
            <TokenAmountDisplay value={formattedAmount} />
            {refundCount > 1 && (
              <Text fontSize="xs" color="gray.500">
                {t('refunds.table.multipleTransactions', { count: refundCount })}
              </Text>
            )}
          </Box>
        )
      },
    },
    {
      key: 'refund_tx_hash',
      title: t('refunds.table.txHash'),
      width: '15%',
      sortable: true,
      render: (value, row) => {
        if (!value) return <Text fontSize="xs" color="gray.500">N/A</Text>

        const explorerUrl = getExplorerTxUrl(resolveRefundChainId(row), value)

        return (
          <Box display="flex" alignItems="center" gap={2}>
            <Tooltip label={t('refunds.actions.copyTxHash')}>
              <Code
                fontSize="xs"
                cursor="pointer"
                _hover={copyableCodeStyles.hover}
                _active={copyableCodeStyles.active}
                onClick={() => copyToClipboard(value, 'TX Hash')}
              >
                {truncateHash(value)}
              </Code>
            </Tooltip>
            {explorerUrl && (
              <Tooltip label={t('refunds.actions.viewOnExplorer')}>
                <IconButton
                  aria-label="View on explorer"
                  icon={<ExternalLinkIcon fontSize="12px" />}
                  size="xs"
                  colorScheme="blue"
                  variant="ghost"
                  onClick={() => window.open(explorerUrl, '_blank')}
                />
              </Tooltip>
            )}
          </Box>
        )
      },
    },
    {
      key: 'chain_id',
      title: t('refunds.table.chain'),
      width: '10%',
      sortable: true,
      render: (_, row) => (
        <ChainBadge
          chainId={resolveRefundChainId(row)}
          chainCode={row.chain_code ?? (row as any).chainCode}
          chainName={row.chain_name ?? (row as any).chainName}
          chainNumericId={row.chain_numeric_id ?? (row as any).chainNumericId}
          isTestnet={row.isTestnet ?? (row as any).is_testnet}
        />
      ),
    },
    {
      key: 'status',
      title: t('refunds.table.status'),
      width: '10%',
      sortable: true,
      render: (value, row) => (
        <Box>
          <StatusBadge statusType="refund" status={value} label={getStatusText(value)} />
          {value === 'ros_pending' && row.verification_soft_retry_count && row.verification_soft_retry_count > 0 && (
            <Text fontSize="xs" color="orange.500" mt={1}>
              {t('refunds.table.retryAttempts', { count: row.verification_soft_retry_count })}
            </Text>
          )}
        </Box>
      ),
    },
    {
      key: 'is_verified',
      title: t('refunds.table.verified'),
      width: '12%',
      sortable: true,
      render: (value, row) => (
        <Box>
          <VerifiedBadge isVerified={value} verifiedAt={row.verified_at} />
          {!value && row.last_verification_check_at && (
            <Text fontSize="xs" color="gray.500" mt={1}>
              {t('refunds.table.lastCheck')}: {formatTime(row.last_verification_check_at)}
            </Text>
          )}
        </Box>
      ),
    },
    {
      key: 'created_at',
      title: t('refunds.table.created'),
      width: '11%',
      sortable: true,
      render: (value) => (
        <Text fontSize="xs">
          {value ? formatDate(value) : 'N/A'}
        </Text>
      ),
    },
  ]

  // Define filter configuration
  const filterConfigs: FilterConfig[] = [
    {
      key: 'search',
      type: 'text',
      label: 'Search',
      placeholder: t('refunds.searchPlaceholder'),
      width: '300px',
    },
    {
      key: 'status',
      type: 'select',
      label: 'Status',
      placeholder: t('refunds.filterByStatus'),
      width: '150px',
      options: [
        { value: 'ros_pending', label: t('refunds.status.pending') },
        { value: 'ros_refunded', label: t('refunds.status.refunded') },
        { value: 'ros_failed', label: t('refunds.status.failed') },
      ],
    },
    {
      key: 'chainId',
      type: 'select',
      label: 'Chain',
      placeholder: t('refunds.filterByChain'),
      width: '150px',
      options: getChainsByNetwork(currentEnv).map(chain => ({
        value: String(chain.numericChainId),
        label: chain.shortName,
      })),
    },
    {
      key: 'isVerified',
      type: 'select',
      label: 'Verified',
      placeholder: t('refunds.filterByVerified'),
      width: '150px',
      options: [
        { value: 'true', label: 'Verified' },
        { value: 'false', label: 'Unverified' },
      ],
    },
    {
      key: 'merchantId',
      type: 'text',
      label: 'Merchant',
      placeholder: t('refunds.filterByMerchant'),
      width: '200px',
    },
    {
      key: 'dateFrom',
      type: 'date',
      label: t('refunds.dateFrom'),
      width: '150px',
    },
    {
      key: 'dateTo',
      type: 'date',
      label: t('refunds.dateTo'),
      width: '150px',
    },
  ]

  // Define row actions
  const getRowActions = (row: RefundOperation): ActionButton[] => {
    const actions: ActionButton[] = []

    // View Details action - primary action, always first
    actions.push({
      label: t('refunds.actions.viewDetails'),
      icon: <FiInfo />,
      tooltip: t('refunds.actions.viewDetails'),
      onClick: () => handleViewDetails(row),
      size: 'xs',
      variant: 'ghost',
      colorScheme: 'blue',
      iconOnly: true,
      ariaLabel: t('refunds.actions.viewDetails'),
    })

    // Verify action - only for unverified refunds that are not failed, second position
    if (!row.is_verified && row.status !== 'ros_failed') {
      actions.push({
        label: t('refunds.actions.verify'),
        icon: <FiCheck />,
        tooltip: t('refunds.actions.verify'),
        onClick: () => handleVerify(row),
        size: 'xs',
        variant: 'ghost',
        colorScheme: 'green',
        iconOnly: true,
        ariaLabel: t('refunds.actions.verify'),
      })
    }

    if (onRefundSelect) {
      const viewLabel = t('common.view')
      actions.push({
        label: viewLabel,
        icon: <FiEye />,
        tooltip: viewLabel,
        onClick: () => onRefundSelect(row),
        size: 'xs',
        variant: 'ghost',
        colorScheme: 'gray',
        iconOnly: true,
        ariaLabel: viewLabel,
      })
    }

    return actions
  }

  // Handle pagination
  const paginationConfig = data?.pagination ? {
    page,
    pageSize,
    total: data.pagination.total,
    pageSizeOptions: [10, 20, 50, 100],
  } : undefined

  // Handle export
  const handleExport = async () => {
    try {
      const { blob, filename } = await refundService.exportCsv({
        search: filters.search || '',
        status: filters.status || '',
        chain_id: filters.chainId || undefined,
        merchant_id: filters.merchantId || undefined,
        is_verified: normalizedIsVerified,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
      }, currentEnv)
      triggerBrowserDownload(blob, filename)
      showToastSuccess({
        title: t('common.exportCsv'),
        description: t('common.export.downloaded'),
      })
    } catch (error) {
      showToastError({
        title: t('common.exportCsv'),
        description: error instanceof Error ? error.message : t('common.operationFailed'),
      })
    }
  }

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        {/* DataTable */}
        <DataTable
          data={data?.data || []}
          loading={isLoading}
          error={error}
          columns={columns}
          filters={showFilters ? filterConfigs : undefined}
          onFiltersChange={setFilters}
          showFilterBar={showFilters}
          sortable={true}
          onSortChange={setSort}
          pagination={paginationConfig}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          actions={getRowActions}
          actionsColumnWidth="10%"
          size="sm"
          emptyMessage={t('refunds.noRefundsFound')}
          showRefresh={true}
          onRefresh={refetch}
          refreshing={isFetching}
          showExport={true}
          onExport={handleExport}
          mobileBreakpoint="md"
        />
      </VStack>

      {/* Verification Confirmation Dialog */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={dialogBg}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('refunds.verify.title')}
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack align="stretch" spacing={4}>
                <Text>{t('refunds.verify.message')}</Text>

                {selectedRefund && (
                  <Box p={3} bg={infoBg} borderRadius="md">
                    <Text fontSize="sm" color={infoTextColor}>
                      <strong>Order ID:</strong> {selectedRefund.orderId || selectedRefund.order_id}
                    </Text>
                    <Text fontSize="sm" color={infoTextColor}>
                      <strong>Amount:</strong> {formatRefundAmount(
                        selectedRefund.total_refund_amount || selectedRefund.refund_amount || '0',
                        resolveRefundChainId(selectedRefund),
                        selectedRefund.token_address,
                        selectedRefund.token_symbol,
                        selectedRefund.tokenDecimals ?? selectedRefund.token_decimals
                      )}
                    </Text>
                    <Text fontSize="sm" color={infoTextColor}>
                      <strong>TX Hash:</strong> {selectedRefund.refund_tx_hash ? truncateHash(selectedRefund.refund_tx_hash, 12) : 'N/A'}
                    </Text>
                  </Box>
                )}

                <Checkbox
                  isChecked={forceVerification}
                  onChange={(e) => setForceVerification(e.target.checked)}
                >
                  {t('refunds.verify.forceOption')}
                </Checkbox>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="green"
                onClick={confirmVerify}
                ml={3}
                isLoading={verifyMutation.isPending}
              >
                {t('refunds.actions.verify')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Refund Details Modal */}
      <RefundDetailsModal
        isOpen={isDetailsOpen}
        onClose={onDetailsClose}
        refundId={selectedRefundIdForDetails}
      />
    </Box>
  )
}
